/* =========================================================================
 * ResearchImpact — homepage section.
 *
 * Per brief: explain WHY participation matters. Five use-cases for the data,
 * each shown as an icon+title+body card with a subtle teal environmental
 * accent. Material Icons used inline for zero-asset overhead.
 * ========================================================================= */

import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";

import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";

const USE_CASES = [
  {
    icon: "filter_drama",
    title: "Atmospheric studies",
    body: "Barometric pressure series at finer geographic resolution than traditional weather stations — useful for storm tracking, microclimate analysis, and altitude inference.",
  },
  {
    icon: "directions_run",
    title: "Mobility & transport",
    body: "Accelerometer, gyroscope, and GPS traces tagged by activity mode (walking / running / driving / cycling / flying / sailing) for transport-mode and human-mobility studies.",
  },
  {
    icon: "location_city",
    title: "Urban analysis",
    body: "Ambient light, sound, and motion patterns help urban planners study walkability, noise pollution, and activity flows in city centers.",
  },
  {
    icon: "park",
    title: "Environmental monitoring",
    body: "Areas with sparse traditional infrastructure benefit most — community sensors fill spatial gaps where ground stations don't exist.",
  },
  {
    icon: "school",
    title: "Academic research",
    body: "Anonymized datasets are licensed to universities for environmental science, public health, and human-mobility research.",
  },
];

export function ResearchImpact() {
  return (
    <MKBox
      component="section"
      py={{ xs: 8, md: 12 }}
      px={{ xs: 2, md: 4 }}
      sx={({ palette }) => ({
        borderTop: `1px solid ${palette.grey[200]}`,
        background: `linear-gradient(180deg, ${palette.white.main} 0%, #F0FDFA 100%)`,
      })}
    >
      <Container>
        <Grid container justifyContent="center" textAlign="center" mb={{ xs: 5, md: 7 }}>
          <Grid item xs={12} md={8}>
            <MKTypography
              variant="overline"
              sx={({ palette }) => ({
                color: palette.secondary.main,
                letterSpacing: "0.1em",
                fontWeight: 700,
              })}
            >
              Research impact
            </MKTypography>
            <MKTypography
              variant="h2"
              color="dark"
              mt={1}
              sx={{ fontWeight: 700, letterSpacing: "-0.02em", lineHeight: 1.15 }}
            >
              Where the data goes
            </MKTypography>
            <MKTypography variant="body1" color="text" mt={2} sx={{ maxWidth: 640, mx: "auto" }}>
              The aggregated datasets generated by the contributor network are used to advance real
              research. Contributors see what their data helped do — not just that they shared it.
            </MKTypography>
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          {USE_CASES.map((u) => (
            <Grid item xs={12} sm={6} md={4} key={u.title}>
              <Card
                elevation={0}
                sx={({ palette, borders }) => ({
                  p: 3,
                  height: "100%",
                  border: `1px solid ${palette.grey[200]}`,
                  borderRadius: borders.borderRadius.lg,
                  background: palette.white.main,
                  transition: "transform 0.2s ease, box-shadow 0.2s ease",
                  "&:hover": {
                    transform: "translateY(-3px)",
                    boxShadow: "0 14px 28px -16px rgba(15, 23, 42, 0.18)",
                  },
                })}
              >
                <MKBox
                  sx={({ palette }) => ({
                    width: 48,
                    height: 48,
                    borderRadius: "12px",
                    display: "grid",
                    placeItems: "center",
                    backgroundColor: "rgba(20, 184, 166, 0.1)",
                    color: palette.secondary.main,
                    mb: 2,
                  })}
                >
                  <Icon fontSize="medium">{u.icon}</Icon>
                </MKBox>
                <MKTypography variant="h6" color="dark" fontWeight="bold" mb={1}>
                  {u.title}
                </MKTypography>
                <MKTypography variant="body2" color="text">
                  {u.body}
                </MKTypography>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    </MKBox>
  );
}
